import { useTranslation } from "react-i18next";
import { PaymentMethod } from "../../../redux/apiSpecifications/apiSubjects";
import { useMemo } from "react";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { CellWrapper, IconButtonCellWrapper } from "../../../style/CellWrapper";
import { TableWrapper } from "../../../style/TableWrapper";
import { IconButtonWithConfirm } from "../../Inputs/IconButtons";
import { IconDelete, IconPracticesRejected } from "../../../config/icons";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { MRT_Localization_IT } from "material-react-table/locales/it";
import { MRT_Localization_EN } from "material-react-table/locales/en";

interface ISubjectPaymentMethodsListProps {
  paymentMethods?: PaymentMethod[];
  onSelectPaymentMethod?: (item: SubjectPaymentMethodsListItem) => void;
  onDeletePaymentMethod?: (item: SubjectPaymentMethodsListItem) => void;
}

const SubjectPaymentMethodsList = ({
  paymentMethods,
  onSelectPaymentMethod,
  onDeletePaymentMethod,
}: ISubjectPaymentMethodsListProps) => {
  const { t } = useTranslation();

  const { language } = useSelector((state: RootState) => state.user) || {};

  const items = useMemo(
    () =>
      paymentMethods?.map((item) => {
        return {
          ...item,
          typeDescription: t(`subject-payment-method-type-${item.type}`)!,
        } as SubjectPaymentMethodsListItem;
      }) || [],
    [paymentMethods]
  );

  const columns = useMemo<
    MRT_ColumnDef<SubjectPaymentMethodsListItem>[]
  >(() => {
    return [
      {
        accessorKey: "typeDescription",
        header: t("subject-payment-method-list-type"),
        grow: true,
        Cell: ({ row }) => (
          <CellWrapper>{row.original.typeDescription}</CellWrapper>
        ),
      },
      {
        accessorKey: "iban",
        header: t("subject-payment-method-list-iban"),
        grow: true,
        Cell: ({ row }) => <CellWrapper>{row.original.iban}</CellWrapper>,
      },
      {
        header: "Actions",
        Header: <span></span>,
        size: 50,
        grow: false,
        enableColumnOrdering: false,
        Cell: ({ row }) => (
          <IconButtonCellWrapper>
            <IconButtonWithConfirm
              onClick={(event) => {
                handleOnDeletePaymentMethod(row.original);
                event?.stopPropagation();
              }}
              popconfirmOption={{
                title: t("MESSAGES.confirm-delete-subject-payment-method"),
                placement: "leftTop",
              }}
            >
              <IconPracticesRejected />
            </IconButtonWithConfirm>
          </IconButtonCellWrapper>
        ),
      },
    ];
  }, [paymentMethods, language]);

  const handleOnSelectPaymentMethod = (item: SubjectPaymentMethodsListItem) => {
    if (onSelectPaymentMethod) onSelectPaymentMethod(item);
  };

  const handleOnDeletePaymentMethod = (item: SubjectPaymentMethodsListItem) => {
    if (onDeletePaymentMethod) onDeletePaymentMethod(item);
  };

  return (
    <TableWrapper>
      <MaterialReactTable
        columns={columns}
        data={items}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableHiding={false}
        enableFilters={true}
        enableColumnActions={false}
        enableColumnDragging={false}
        enableSorting={true}
        enableTopToolbar={true}
        localization={
          language === "it" ? MRT_Localization_IT : MRT_Localization_EN
        }
        muiTableBodyRowProps={({ row }) => ({
          onClick: (event) => {
            handleOnSelectPaymentMethod(row.original);
          },
          sx: {
            cursor: onSelectPaymentMethod !== undefined ? "pointer" : "default",
          },
        })}
      />
    </TableWrapper>
  );
};

export default SubjectPaymentMethodsList;

export type SubjectPaymentMethodsListItem = PaymentMethod & {
  typeDescription?: string;
};
